/**
 * 패널 플래닝 모듈 트리 — 가상 스크롤용 평면 행 목록 생성
 *
 * === ID 규칙 ===
 * - item.id:      Tree 노드 식별자 (PanelEntityId.mapKey 또는 숫자 seq)
 * - item.selfSeq: DB seq (entityId.selfSeq)
 * - item.selfDisplayId: 화면 표시 번호 (entityId.selfDisplayId)
 * - item.parentSeq / item.parentDisplayId: 계층 정보 (entityId.*)
 *
 * resolve* 함수들은 모두 PanelEntityId 기반으로 동작하도록 통일.
 */
import { PanelEntityId } from '@/features/panel-planning/utils/PanelEntityId';
import { getPanelTreeModuleChildren } from './panelTreeModuleChildrenIndex.js';
import { findPanelTreeInsertIndex } from './panelModuleSeqOrder.js';

function resolveTreeNodeChildren(item) {
  if (!item) {
    return [];
  }
  if (item.lazyModules) {
    const lazyChildren = getPanelTreeModuleChildren(item.id);
    if (lazyChildren.length) {
      return lazyChildren;
    }
  }
  return item.children || [];
}

export function flattenModuleTreeRows(nodes, expandedIds) {
  const rows = [];
  const isExpanded = (id) => {
    if (!id) return false;
    if (expandedIds instanceof Set) return expandedIds.has(id);
    return expandedIds[id] === true;
  };

  const nodeHasChildren = (item) => {
    if (item.children?.length > 0) return true;
    if (item.moduleChildCount > 0) return true;
    if (item.lazyModules) return true;
    return false;
  };

  const walk = (nodeList, level, parentId) => {
    if (!nodeList || !Array.isArray(nodeList)) return;
    nodeList.forEach((item, index) => {
      const hasChildren = nodeHasChildren(item);
      const expanded = hasChildren && isExpanded(item.id);
      rows.push({
        kind: 'node',
        item,
        level,
        parentId,
        hasChildren,
        expanded,
        isLastChild: index === nodeList.length - 1
      });
      if (hasChildren && expanded) {
        const children = resolveTreeNodeChildren(item);
        if (children.length > 0) {
          walk(children, level + 1, item.id);
        }
      }
    });
  };

  walk(nodes, 0, null);
  return rows;
}

export function collectExpandableTreeNodeIds(nodes) {
  const ids = [];
  const nodeHasChildren = (item) => {
    if (item.children?.length > 0) return true;
    if (item.moduleChildCount > 0) return true;
    if (item.lazyModules) return true;
    return false;
  };
  const walk = (nodeList) => {
    if (!nodeList || !Array.isArray(nodeList)) return;
    nodeList.forEach((item) => {
      if (nodeHasChildren(item) && item.id != null) {
        ids.push(item.id);
        if (item.children?.length) {
          walk(item.children);
        }
      }
    });
  };
  walk(nodes);
  return ids;
}

export function defaultExpandedForTreeNode(item) {
  if (!item) return false;
  // 초기 로드·사이트 전환 — Array/String 모두 접힘 (사용자가 펼침)
  return false;
}

export function createDefaultExpandedState(nodes, expanded = null) {
  const state = {};
  const nodeHasChildren = (item) => {
    if (item.children?.length > 0) return true;
    if (item.moduleChildCount > 0) return true;
    if (item.lazyModules) return true;
    return false;
  };
  const walk = (nodeList) => {
    if (!nodeList || !Array.isArray(nodeList)) return;
    nodeList.forEach((item) => {
      if (nodeHasChildren(item) && item.id != null) {
        state[item.id] =
          expanded === null ? defaultExpandedForTreeNode(item) : expanded;
        if (item.children?.length) {
          walk(item.children);
        }
      }
    });
  };
  walk(nodes);
  return state;
}

// ── ID Resolve 함수들 (PanelEntityId 기반 통합) ──

/**
 * 트리 노드 → PanelEntityId 생성
 * @param {Object} item - 트리 노드
 * @param {string} context - 사용 맥락 ('resolve-delete-key', 'resolve-api-seq', etc.)
 * @returns {PanelEntityId|null}
 */
function toEntityId(item, context) {
  if (!item) return null;
  return PanelEntityId.fromTreeNode(item, context);
}

/**
 * 트리 String 노드 → DELETE /solar-string/{id} 용 DB seq
 * @param {Object} item
 * @param {Object} [ctx]
 * @returns {number|null}
 */
export function resolveSolarStringSeqFromTreeItem(item, ctx = {}) {
  const pid = toEntityId(item, 'resolve-solar-string-seq');
  if (!pid) return null;

  // selfSeq가 유효하면 바로 반환
  if (pid.selfSeq > 0) return pid.selfSeq;

  // fallback: ctx에서 displayId로 lookup
  const displayId = pid.selfDisplayId;
  const { flattenedSiteDesign, solarStrings } = ctx;

  if (displayId != null && flattenedSiteDesign?.byString) {
    for (const info of flattenedSiteDesign.byString.values()) {
      if (String(info.stringId) === displayId || String(info.solarStringId) === displayId) {
        return info.id;
      }
    }
  }

  if (displayId != null && Array.isArray(solarStrings)) {
    const match = solarStrings.find(
      s => String(s.stringId) === displayId || String(s.solarStringId) === displayId
    );
    if (match?.id != null) return match.id;
  }

  return null;
}

/**
 * 트리 Array 노드 → PUT /solarStrings/{id}/solarArray 용 DB seq
 * @param {Object} item
 * @param {Object} [ctx]
 * @returns {number|null}
 */
const MAX_PLAUSIBLE_ARRAY_SEQ = 1000000000;

function isPlausibleArrayDbSeq(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && n < MAX_PLAUSIBLE_ARRAY_SEQ;
}

function lookupArraySeqByDisplayId(flattenedSiteDesign, displayId) {
  if (displayId == null || !flattenedSiteDesign?.byArray) {
    return null;
  }
  for (const info of flattenedSiteDesign.byArray.values()) {
    if (String(info.arrayId) === String(displayId)) {
      return info.id;
    }
  }
  return null;
}

export function resolveSolarArraySeqFromTreeItem(item, ctx = {}) {
  if (item?.solarArraySeq != null && item.solarArraySeq !== '') {
    if (isPlausibleArrayDbSeq(item.solarArraySeq)) {
      return Number(item.solarArraySeq);
    }
  }

  const pid = toEntityId(item, 'resolve-solar-array-seq');
  if (!pid) return null;

  // selfSeq 우선 (Date.now() 임시 id 등 비정상 값 제외)
  if (isPlausibleArrayDbSeq(pid.selfSeq)) {
    return pid.selfSeq;
  }

  // fallback: displayId → DB seq lookup
  const displayId = pid.selfDisplayId;
  const { flattenedSiteDesign } = ctx;

  if (displayId != null && flattenedSiteDesign?.byArray) {
    const fromDisplay = lookupArraySeqByDisplayId(flattenedSiteDesign, displayId);
    if (fromDisplay != null) {
      return fromDisplay;
    }
    for (const info of flattenedSiteDesign.byArray.values()) {
      if (String(info.id) === displayId || String(info.solarArrayId) === displayId) {
        return info.id;
      }
    }
  }

  return null;
}

/**
 * 트리 Module 노드 → DELETE /solar-module/{id} 용 키
 * selfSeq (DB seq) 우선, fallback으로 displayId
 *
 * @param {Object} item
 * @param {Object} [ctx]
 * @returns {number|string|null}
 */
export function resolveSolarModuleDeleteKeyFromTreeItem(item, ctx = {}) {
  const pid = toEntityId(item, 'resolve-module-delete-key');
  if (!pid) return null;

  // selfSeq (DB seq) 우선
  if (pid.selfSeq > 0) return pid.selfSeq;

  // fallback: displayId
  const businessId = pid.selfDisplayId;
  const { flattenedSiteDesign } = ctx;

  if (businessId != null && flattenedSiteDesign?.byModule) {
    for (const info of flattenedSiteDesign.byModule.values()) {
      if (String(info.moduleId) === businessId) return info.id;
    }
    return businessId;
  }

  return null;
}

// ── Tree 조작 함수들 (immutable) ──

export function removeTreeNodeById(nodes, nodeId) {
  if (!nodes || !Array.isArray(nodes) || nodeId == null) return nodes || [];
  const target = String(nodeId);
  const filterList = (list) => {
    if (!list || !Array.isArray(list)) return [];
    return list
      .filter((n) => String(n.id) !== target)
      .map((n) => ({ ...n, children: filterList(n.children) }));
  };
  return filterList(nodes);
}

/** Array 노드 children에 String 노드 추가 (immutable) */
export function appendStringToTree(nodes, arrayNodeId, stringNode) {
  if (!nodes || !Array.isArray(nodes) || arrayNodeId == null || !stringNode) {
    return nodes || [];
  }
  const target = String(arrayNodeId);
  const mapList = list =>
    (list || []).map(node => {
      if (String(node.id) === target && node.type === 'array') {
        const children = [...(node.children || [])];
        const idx = findPanelTreeInsertIndex(children, stringNode);
        children.splice(idx, 0, stringNode);
        return {
          ...node,
          children
        };
      }
      if (node.children?.length) {
        return { ...node, children: mapList(node.children) };
      }
      return node;
    });
  return mapList(nodes);
}

export function updateTreeNodeNameById(nodes, nodeId, newName) {
  if (!nodes || !Array.isArray(nodes) || nodeId == null) return nodes || [];
  const target = String(nodeId);
  const mapList = (list) =>
    (list || []).map((n) => {
      if (String(n.id) === target) return { ...n, name: newName };
      if (n.children?.length) return { ...n, children: mapList(n.children) };
      return n;
    });
  return mapList(nodes);
}

/** String 이름·표시 번호(stringId) 동시 갱신 — @deprecated updateTreeNodeEntityDisplayById 사용 */
export function updateTreeNodeStringDisplayById(nodes, nodeId, { newName, stringId }) {
  return updateTreeNodeEntityDisplayById(nodes, nodeId, {
    entityType: 'string',
    newName,
    displayId: stringId
  });
}

/** 트리 노드 이름·표시 번호(Array|stringId / Module|moduleId) 동시 갱신 */
export function updateTreeNodeEntityDisplayById(nodes, nodeId, { entityType, newName, displayId }) {
  if (!nodes || !Array.isArray(nodes) || nodeId == null || !entityType) {
    return nodes || [];
  }
  const target = String(nodeId);
  const displayIdStr = displayId != null && displayId !== '' ? String(displayId) : null;

  const patchNode = node => {
    if (String(node.id) !== target || node.type !== entityType) {
      return node;
    }
    const next = { ...node, name: newName };
    if (displayIdStr) {
      if (entityType === 'array') {
        next.arrayId = displayIdStr;
        next.solarArrayId = node.solarArraySeq ?? node.solarArrayId;
      } else if (entityType === 'string') {
        next.stringId = displayIdStr;
        next.solarStringId = displayIdStr;
      } else if (entityType === 'module') {
        next.moduleId = displayIdStr;
        next.solarModuleId = displayIdStr;
      }
    }
    return next;
  };

  const mapList = list =>
    (list || []).map(n => {
      if (String(n.id) === target && n.type === entityType) {
        return patchNode(n);
      }
      if (n.children?.length) {
        return { ...n, children: mapList(n.children) };
      }
      return n;
    });

  return mapList(nodes);
}

export function moveModuleNodeToString(nodes, moduleNodeId, targetStringNodeId) {
  if (!nodes || !Array.isArray(nodes) || moduleNodeId == null || targetStringNodeId == null) {
    return nodes || [];
  }

  const moduleKey = String(moduleNodeId);
  const stringKey = String(targetStringNodeId);
  let moved = null;

  const extract = (list) =>
    (list || []).reduce((acc, node) => {
      if (String(node.id) === moduleKey) {
        moved = node;
        return acc;
      }
      if (node.children?.length) {
        acc.push({ ...node, children: extract(node.children) });
      } else {
        acc.push(node);
      }
      return acc;
    }, []);

  const insert = (list) =>
    (list || []).map((node) => {
      if (node.type === 'string' && String(node.id) === stringKey && moved) {
        return { ...node, children: [...(node.children || []), moved] };
      }
      if (node.children?.length) {
        return { ...node, children: insert(node.children) };
      }
      return node;
    });

  const without = extract(nodes);
  if (!moved) return nodes;
  return insert(without);
}

/** depth-first id 목록 서명 — append/remove-only 증분 변경 판별용 */
export function computeModuleTreeSignature(nodes) {
  const parts = [];
  const walk = nodeList => {
    if (!nodeList || !Array.isArray(nodeList)) {
      return;
    }
    nodeList.forEach(item => {
      if (item?.id != null) {
        parts.push(String(item.id));
      }
      if (item?.children?.length > 0) {
        walk(item.children);
      }
    });
  };
  walk(nodes || []);
  return parts.join('|');
}

/** 기존 노드 순서 유지 + 새 id만 추가된 경우 (String/Array append 등) */
export function isAppendOnlyTreeSignatureChange(prevSig, nextSig) {
  if (!prevSig || !nextSig || nextSig.length <= prevSig.length) {
    return false;
  }
  const prevIds = prevSig.split('|').filter(Boolean);
  const nextIds = nextSig.split('|').filter(Boolean);
  if (nextIds.length <= prevIds.length) {
    return false;
  }
  let prevIndex = 0;
  for (let i = 0; i < nextIds.length && prevIndex < prevIds.length; i++) {
    if (nextIds[i] === prevIds[prevIndex]) {
      prevIndex += 1;
    }
  }
  return prevIndex === prevIds.length;
}

/** 기존 노드 순서 유지 + 일부 id만 제거된 경우 (String/Array delete 등) */
export function isRemoveOnlyTreeSignatureChange(prevSig, nextSig) {
  if (!prevSig || !nextSig || nextSig.length >= prevSig.length) {
    return false;
  }
  const prevIds = prevSig.split('|').filter(Boolean);
  const nextIds = nextSig.split('|').filter(Boolean);
  if (nextIds.length >= prevIds.length) {
    return false;
  }
  let prevIndex = 0;
  for (let i = 0; i < nextIds.length; i++) {
    while (prevIndex < prevIds.length && prevIds[prevIndex] !== nextIds[i]) {
      prevIndex += 1;
    }
    if (prevIndex >= prevIds.length) {
      return false;
    }
    prevIndex += 1;
  }
  return true;
}

/** append-only 또는 remove-only — 펼침 상태 유지 가능한 증분 변경 */
export function isIncrementalTreeSignatureChange(prevSig, nextSig) {
  if (!prevSig || !nextSig) {
    return false;
  }
  if (prevSig === nextSig) {
    return true;
  }
  const prevIds = prevSig.split('|').filter(Boolean);
  const nextIds = nextSig.split('|').filter(Boolean);
  if (nextIds.length > prevIds.length) {
    return isAppendOnlyTreeSignatureChange(prevSig, nextSig);
  }
  if (nextIds.length < prevIds.length) {
    return isRemoveOnlyTreeSignatureChange(prevSig, nextSig);
  }
  return false;
}
